import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, CalendarPlus, Copy, Download, FileText, PlayCircle, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Action, Cell, DataTable, EmptyState, Field, PageHeader, Panel, PanelHeader, Row, StatusBadge } from "./ui";

const TABS = [
  { key: "week", label: "Programação Semanal" },
  { key: "topics", label: "Temas de Reunião" },
  { key: "templates", label: "Grades Padrão" },
  { key: "history", label: "Histórico" },
  { key: "indicators", label: "Indicadores" },
];

const STATUS_TONE = { scheduled: "neutral", in_progress: "pending", done: "active", cancelled: "alert" };

export const formatDay = (value) => (value ? String(value).slice(0, 10).split("-").reverse().join("/") : "—");
const addDays = (iso, days) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const mondayOf = (d = new Date()) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return copy.toISOString().slice(0, 10);
};

const useGridOptions = () => {
  const { api } = useAuth();
  const [options, setOptions] = useState(null);
  useEffect(() => {
    api
      .get("/portal/meeting-grid/options")
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return options;
};

const OccurrenceCard = ({ item, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(item)}
    data-testid={`grid-occurrence-${item.id}`}
    className="w-full rounded-xl border border-border bg-white p-3 text-left transition-colors hover:border-sage-600"
  >
    <p className="text-[14.5px] font-semibold text-ink-900">{item.topic_name}</p>
    <p className="text-[13px] text-ink-500">
      {item.start_time}–{item.end_time} · {item.responsible_name || "Sem responsável"}
    </p>
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <StatusBadge tone={STATUS_TONE[item.status]}>{item.status_label}</StatusBadge>
      {item.pending_confirmation ? <StatusBadge tone="pending">Pendente de confirmação</StatusBadge> : null}
      {item.extraordinary ? <StatusBadge tone="neutral">Extraordinária</StatusBadge> : null}
      {item.changed_from_template ? <StatusBadge tone="neutral">Alterada em relação à grade padrão</StatusBadge> : null}
      {item.attendance_registered ? (
        <span className="text-[12.5px] text-ink-500">Presentes: {item.attendance_count}</span>
      ) : null}
    </div>
  </button>
);

const ExportPdfModal = ({ options, ward, start, end, onClose }) => {
  const { api } = useAuth();
  const locked = Boolean(options?.ward_locked);
  const [choice, setChoice] = useState(ward);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data, headers } = await api.get("/portal/meeting-grid/week/pdf", {
        params: { ward: choice, start },
        responseType: "blob",
      });
      const match = /filename="?([^"]+)"?/.exec(headers?.["content-disposition"] || "");
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = match?.[1] || "Erimus_Grade_Reunioes.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF da grade gerado com sucesso.");
      onClose();
    } catch (err) {
      toast.error("Não foi possível gerar o PDF. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const wardOptions = locked ? [{ value: ward, label: options?.wards?.find((w) => w.value === ward)?.label || "Sua ala" }] : [...(options?.wards || []), { value: "AMBAS", label: "Ambas as alas" }];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/40 sm:items-center sm:p-6">
      <div data-testid="grid-export-modal" className="w-full max-w-md space-y-4 rounded-t-3xl bg-white p-6 sm:rounded-3xl">
        <h2 className="font-display text-[20px] font-semibold text-ink-900">Exportar grade em PDF</h2>
        <p className="text-[13.5px] text-ink-500">
          Semana de {formatDay(start)} a {formatDay(end)} · A4 paisagem, sem dados de pacientes.
        </p>
        <Field label="Ala" htmlFor="export-ward">
          <select
            id="export-ward"
            data-testid="grid-export-ward"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            disabled={locked}
            className="h-11 w-full rounded-xl border border-border px-3.5 text-[14.5px] disabled:bg-sand"
          >
            {wardOptions.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
        </Field>
        <Action data-testid="grid-export-submit" icon={Download} onClick={generate} disabled={busy}>
          {busy ? "Gerando PDF…" : "Gerar PDF"}
        </Action>
        <button type="button" onClick={onClose} data-testid="grid-export-close" className="w-full rounded-xl border border-border py-2.5 text-[14.5px] font-semibold text-ink-700">
          Fechar
        </button>
      </div>
    </div>
  );
};

const WeekView = ({ options, ward, setWard }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [start, setStart] = useState(mondayOf());
  const [filters, setFilters] = useState({ responsible_id: "", topic_id: "", status: "all" });
  const [data, setData] = useState(null);
  const [extra, setExtra] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/portal/meeting-grid/week", { params: { ward, start, ...filters } });
      setData(payload);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setData({ items: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward, start, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);
  const action = async (path, params, message) => {
    try {
      await api.post(path, null, { params: { ward, start, ...params } });
      toast.success(message);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Panel testId="grid-week-panel" className="space-y-5">
      <PanelHeader
        label={data?.ward_label || "Grade"}
        title="Programação Semanal"
        hint={data ? `${formatDay(data.start)} a ${formatDay(data.end)}` : "Carregando…"}
        actions={
          <div className="flex flex-wrap gap-2">
            <Action size="sm" variant="ghost" data-testid="grid-prev-week" onClick={() => setStart(addDays(start, -7))}>
              Semana anterior
            </Action>
            <Action size="sm" variant="ghost" data-testid="grid-today" onClick={() => setStart(mondayOf())}>
              Hoje
            </Action>
            <Action size="sm" variant="ghost" data-testid="grid-next-week" onClick={() => setStart(addDays(start, 7))}>
              Próxima semana
            </Action>
            <Action size="sm" variant="secondary" icon={Download} data-testid="grid-export-pdf" onClick={() => setExporting(true)}>
              Exportar PDF
            </Action>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        {options?.ward_locked ? (
          <span data-testid="grid-ward-scope" className="inline-flex h-10 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700">
            {data?.ward_label}
          </span>
        ) : (
          (options?.wards || []).map((w) => (
            <button
              key={w.value}
              type="button"
              data-testid={`grid-ward-${w.value}`}
              onClick={() => setWard(w.value)}
              className={`rounded-full px-4 py-2 text-[14px] font-semibold ${
                ward === w.value ? "bg-sage-600 text-white" : "bg-sand text-ink-700"
              }`}
            >
              {w.label}
            </button>
          ))
        )}
        <select
          data-testid="grid-filter-responsible"
          aria-label="Responsável"
          value={filters.responsible_id}
          onChange={(e) => setFilters({ ...filters, responsible_id: e.target.value })}
          className="h-10 rounded-xl border border-border bg-white px-3 text-[14px]"
        >
          <option value="">Responsável: todos</option>
          {(options?.responsibles || []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <select
          data-testid="grid-filter-topic"
          aria-label="Tema"
          value={filters.topic_id}
          onChange={(e) => setFilters({ ...filters, topic_id: e.target.value })}
          className="h-10 rounded-xl border border-border bg-white px-3 text-[14px]"
        >
          <option value="">Tema: todos</option>
          {(options?.topics || []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          data-testid="grid-filter-status"
          aria-label="Situação"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="h-10 rounded-xl border border-border bg-white px-3 text-[14px]"
        >
          <option value="all">Situação: todas</option>
          {(options?.status || []).map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
          <option value="pending">Pendente de confirmação</option>
        </select>
        {data?.can_manage ? (
          <>
            <Action size="sm" icon={CalendarPlus} data-testid="grid-new-extra" onClick={() => setExtra(true)}>
              Reunião extraordinária
            </Action>
            <Action
              size="sm"
              variant="secondary"
              icon={Copy}
              data-testid="grid-copy-week"
              onClick={() => action("/portal/meeting-grid/week/copy-previous", {}, "Semana anterior copiada.")}
            >
              Copiar semana anterior
            </Action>
            <Action
              size="sm"
              variant="ghost"
              icon={RotateCcw}
              data-testid="grid-restore-week"
              onClick={() => {
                if (window.confirm("Restaurar a programação padrão desta ala? Reuniões já ocorridas, em andamento, realizadas ou canceladas não serão alteradas."))
                  action("/portal/meeting-grid/week/restore-default", {}, "Programação padrão restaurada.");
              }}
            >
              Restaurar programação padrão
            </Action>
          </>
        ) : null}
      </div>

      <div data-testid="grid-week-grid" className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {days.map((day) => {
          const items = (data?.items || []).filter((i) => i.date === day);
          return (
            <div key={day} className="space-y-2.5">
              <p className="text-[13px] font-semibold uppercase tracking-wide text-ink-500">
                {items[0]?.weekday_label || ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"][(new Date(`${day}T12:00:00`).getDay() + 6) % 7]} · {formatDay(day)}
              </p>
              {items.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border p-3 text-[13px] text-ink-400">Sem reuniões</p>
              ) : (
                items.map((item) => (
                  <OccurrenceCard key={item.id} item={item} onOpen={(i) => navigate(`/portal/grade-reunioes/${i.id}`)} />
                ))
              )}
            </div>
          );
        })}
      </div>

      {extra ? (
        <ExtraMeetingModal options={options} ward={ward} onClose={() => setExtra(false)} onSaved={load} />
      ) : null}
      {exporting ? (
        <ExportPdfModal options={options} ward={ward} start={start} end={addDays(start, 6)} onClose={() => setExporting(false)} />
      ) : null}
    </Panel>
  );
};

const ExtraMeetingModal = ({ options, ward, onClose, onSaved }) => {
  const { api } = useAuth();
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    start_time: "09:30",
    duration_minutes: options?.default_duration_minutes || 60,
    topic_id: "",
    responsible_id: "",
    summary: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/portal/meeting-grid/occurrences", { ...form, ward, duration_minutes: Number(form.duration_minutes) });
      toast.success("Reunião extraordinária criada.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/40 sm:items-center sm:p-6">
      <form
        onSubmit={submit}
        data-testid="grid-extra-modal"
        className="max-h-[92vh] w-full max-w-lg space-y-3.5 overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl"
      >
        <h2 className="font-display text-[20px] font-semibold text-ink-900">Reunião extraordinária</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Data" htmlFor="extra-date">
            <input id="extra-date" type="date" data-testid="grid-extra-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 w-full rounded-xl border border-border px-3.5" />
          </Field>
          <Field label="Horário" htmlFor="extra-time">
            <input id="extra-time" type="time" data-testid="grid-extra-time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-11 w-full rounded-xl border border-border px-3.5" />
          </Field>
        </div>
        <Field label="Duração (min)" htmlFor="extra-duration">
          <input id="extra-duration" type="number" min="15" data-testid="grid-extra-duration" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="h-11 w-full rounded-xl border border-border px-3.5" />
        </Field>
        <Field label="Tema" htmlFor="extra-topic">
          <select id="extra-topic" data-testid="grid-extra-topic" value={form.topic_id} onChange={(e) => setForm({ ...form, topic_id: e.target.value })} required className="h-11 w-full rounded-xl border border-border px-3.5">
            <option value="">Selecionar</option>
            {(options?.topics || []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Responsável" htmlFor="extra-responsible">
          <select id="extra-responsible" data-testid="grid-extra-responsible" value={form.responsible_id} onChange={(e) => setForm({ ...form, responsible_id: e.target.value })} required className="h-11 w-full rounded-xl border border-border px-3.5">
            <option value="">Selecionar</option>
            {(options?.responsibles || [])
              .filter((r) => !r.ward || r.ward === ward || !["MASCULINA", "FEMININA"].includes(r.ward))
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {r.role}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Resumo" hint="Opcional" htmlFor="extra-summary">
          <textarea id="extra-summary" rows={3} data-testid="grid-extra-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="w-full rounded-xl border border-border p-3" />
        </Field>
        <Action type="submit" data-testid="grid-extra-submit" disabled={saving}>
          {saving ? "Salvando…" : "Criar reunião"}
        </Action>
        <button type="button" onClick={onClose} data-testid="grid-extra-close" className="w-full rounded-xl border border-border py-2.5 text-[14.5px] font-semibold text-ink-700">
          Fechar
        </button>
      </form>
    </div>
  );
};

const TopicsView = ({ options }) => {
  const { api } = useAuth();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", summary: "", description: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/portal/meeting-grid/topics");
    setItems(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/portal/meeting-grid/topics", form);
      toast.success("Tema criado.");
      setForm({ name: "", summary: "", description: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const upload = async (topicId, file) => {
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/portal/meeting-grid/topics/${topicId}/material`, body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Material enviado.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggle = async (topic) => {
    try {
      await api.patch(`/portal/meeting-grid/topics/${topic.id}`, { name: topic.name, status: topic.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5">
      {options?.can_manage ? (
        <Panel testId="grid-topic-form-panel">
          <PanelHeader label="Biblioteca" title="Novo tema" hint="Os temas são reutilizáveis nas duas alas." />
          <form onSubmit={create} className="mt-4 grid gap-3 md:grid-cols-3">
            <Field label="Nome do tema" htmlFor="topic-name">
              <input id="topic-name" data-testid="grid-topic-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 w-full rounded-xl border border-border px-3.5" />
            </Field>
            <Field label="Resumo / objetivo" htmlFor="topic-summary">
              <input id="topic-summary" data-testid="grid-topic-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="h-11 w-full rounded-xl border border-border px-3.5" />
            </Field>
            <Field label="Descrição do conteúdo" htmlFor="topic-description">
              <input id="topic-description" data-testid="grid-topic-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-11 w-full rounded-xl border border-border px-3.5" />
            </Field>
            <div className="md:col-span-3">
              <Action type="submit" data-testid="grid-topic-submit">
                Cadastrar tema
              </Action>
            </div>
          </form>
        </Panel>
      ) : null}

      <Panel testId="grid-topics-panel" className="space-y-3">
        <PanelHeader label="Biblioteca" title="Temas de Reunião" hint="Materiais em PDF, PPT, PPTX, DOC ou DOCX." />
        {(items || []).length === 0 ? (
          <EmptyState testId="grid-topics-empty" title="Nenhum tema cadastrado" description="Cadastre os temas aplicados aos pacientes para montar as grades." />
        ) : (
          items.map((topic) => (
            <div key={topic.id} data-testid={`grid-topic-${topic.id}`} className="rounded-xl border border-border bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-ink-900">{topic.name}</p>
                  {topic.summary ? <p className="text-[13.5px] text-ink-600">{topic.summary}</p> : null}
                  {topic.description ? <p className="text-[13px] text-ink-500">{topic.description}</p> : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={topic.status === "active" ? "active" : "neutral"}>{topic.status_label}</StatusBadge>
                  {topic.material ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/portal/meeting-grid/topics/${topic.id}/material`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`grid-topic-material-${topic.id}`}
                      className="inline-flex min-h-[38px] items-center gap-2 rounded-full bg-sand px-4 text-[13.5px] font-semibold text-ink-700"
                    >
                      <FileText size={15} aria-hidden="true" /> {topic.material.filename}
                    </a>
                  ) : (
                    <span className="text-[13px] text-ink-400">Sem material</span>
                  )}
                  {options?.can_manage ? (
                    <>
                      <label className="inline-flex min-h-[38px] cursor-pointer items-center gap-2 rounded-full bg-sage-600 px-4 text-[13.5px] font-semibold text-white">
                        <Upload size={15} aria-hidden="true" />
                        {topic.material ? "Substituir material" : "Anexar material"}
                        <input
                          type="file"
                          className="hidden"
                          data-testid={`grid-topic-upload-${topic.id}`}
                          accept=".pdf,.ppt,.pptx,.doc,.docx"
                          onChange={(e) => e.target.files?.[0] && upload(topic.id, e.target.files[0])}
                        />
                      </label>
                      <Action size="sm" variant="ghost" data-testid={`grid-topic-toggle-${topic.id}`} onClick={() => toggle(topic)}>
                        {topic.status === "active" ? "Inativar" : "Ativar"}
                      </Action>
                    </>
                  ) : null}
                </div>
              </div>
              {topic.material_versions > 0 ? (
                <p className="mt-2 text-[12.5px] text-ink-400">{topic.material_versions} versão(ões) anterior(es) preservada(s).</p>
              ) : null}
            </div>
          ))
        )}
      </Panel>
    </div>
  );
};

const TemplatesView = ({ options, ward, setWard }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data: payload } = await api.get("/portal/meeting-grid/templates", { params: { ward } });
    setData(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);
  useEffect(() => {
    load();
  }, [load]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/portal/meeting-grid/templates/${id}`, body);
      toast.success("Grade padrão atualizada.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Panel testId="grid-templates-panel" className="space-y-4">
      <PanelHeader
        label={data?.ward_label || "Grade padrão"}
        title="Grade Padrão por ala"
        hint="Cada ala tem a própria grade: alterar uma nunca altera a outra."
        actions={
          options?.ward_locked ? null : (
            <div className="flex gap-2">
              {(options?.wards || []).map((w) => (
                <button
                  key={w.value}
                  type="button"
                  data-testid={`templates-ward-${w.value}`}
                  onClick={() => setWard(w.value)}
                  className={`rounded-full px-4 py-2 text-[13.5px] font-semibold ${ward === w.value ? "bg-sage-600 text-white" : "bg-sand text-ink-700"}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          )
        }
      />
      <DataTable
        testId="grid-templates-table"
        columns={[
          { key: "day", label: "Dia" },
          { key: "time", label: "Horário" },
          { key: "topic", label: "Tema" },
          { key: "responsible", label: "Responsável" },
        ]}
      >
        {(data?.items || []).map((row) => (
          <Row key={row.id} testId={`grid-template-${row.id}`}>
            <Cell>{row.weekday_label}</Cell>
            <Cell>
              {row.start_time}–{row.end_time}
            </Cell>
            <Cell>
              {options?.can_manage ? (
                <select
                  data-testid={`grid-template-topic-${row.id}`}
                  value={row.topic_id || ""}
                  onChange={(e) => patch(row.id, { topic_id: e.target.value || null })}
                  className="h-10 w-full min-w-[170px] rounded-xl border border-border bg-white px-2.5 text-[13.5px]"
                >
                  <option value="">Sem tema</option>
                  {(options?.topics || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              ) : (
                row.topic_name || "Sem tema"
              )}
            </Cell>
            <Cell>
              {options?.can_manage ? (
                <select
                  data-testid={`grid-template-responsible-${row.id}`}
                  value={row.responsible_id || ""}
                  onChange={(e) => patch(row.id, { responsible_id: e.target.value || null })}
                  className="h-10 w-full min-w-[170px] rounded-xl border border-border bg-white px-2.5 text-[13.5px]"
                >
                  <option value="">Sem responsável</option>
                  {(options?.responsibles || [])
                    .filter((r) => !["MASCULINA", "FEMININA"].includes(r.ward) || r.ward === ward)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} — {r.role}
                      </option>
                    ))}
                </select>
              ) : (
                row.responsible_name || "Sem responsável"
              )}
            </Cell>
          </Row>
        ))}
      </DataTable>
    </Panel>
  );
};

const HistoryView = ({ options }) => {
  const { api } = useAuth();
  const [filters, setFilters] = useState({ ward: "", topic_id: "", responsible_id: "", status: "all", start: "", end: "" });
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/portal/meeting-grid/history", { params: filters })
      .then(({ data }) => setItems(data.items))
      .catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <Panel testId="grid-history-panel" className="space-y-4">
      <PanelHeader label="Grade de Reuniões" title="Histórico de Reuniões" hint="Últimos 30 dias por padrão." />
      <div className="flex flex-wrap gap-2">
        <input type="date" aria-label="Início" data-testid="history-start" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="h-10 rounded-xl border border-border px-3 text-[14px]" />
        <input type="date" aria-label="Fim" data-testid="history-end" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="h-10 rounded-xl border border-border px-3 text-[14px]" />
        {options?.ward_locked ? null : (
          <select data-testid="history-ward" aria-label="Ala" value={filters.ward} onChange={(e) => setFilters({ ...filters, ward: e.target.value })} className="h-10 rounded-xl border border-border px-3 text-[14px]">
            <option value="">Todas as alas</option>
            {(options?.wards || []).map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
        )}
        <select data-testid="history-topic" aria-label="Tema" value={filters.topic_id} onChange={(e) => setFilters({ ...filters, topic_id: e.target.value })} className="h-10 rounded-xl border border-border px-3 text-[14px]">
          <option value="">Tema: todos</option>
          {(options?.topics || []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select data-testid="history-status" aria-label="Situação" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="h-10 rounded-xl border border-border px-3 text-[14px]">
          <option value="all">Situação: todas</option>
          {(options?.status || []).map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
          <option value="pending">Pendente de confirmação</option>
        </select>
      </div>
      {(items || []).length === 0 ? (
        <EmptyState testId="grid-history-empty" title="Nenhuma reunião no período" description="Ajuste os filtros para consultar outros períodos." />
      ) : (
        <DataTable
          testId="grid-history-table"
          columns={[
            { key: "date", label: "Data" },
            { key: "time", label: "Horário" },
            { key: "ward", label: "Ala" },
            { key: "topic", label: "Tema" },
            { key: "responsible", label: "Responsável" },
            { key: "status", label: "Situação" },
            { key: "present", label: "Presentes" },
          ]}
        >
          {items.map((row) => (
            <Row key={row.id} testId={`grid-history-${row.id}`}>
              <Cell>
                <Link to={`/portal/grade-reunioes/${row.id}`} className="font-semibold text-sage-700 hover:underline">
                  {formatDay(row.date)}
                </Link>
              </Cell>
              <Cell>
                {row.start_time}–{row.end_time}
              </Cell>
              <Cell>{row.ward_label}</Cell>
              <Cell>{row.topic_name}</Cell>
              <Cell>{row.responsible_name}</Cell>
              <Cell>{row.pending_confirmation ? "Pendente de confirmação" : row.status_label}</Cell>
              <Cell>{row.attendance_registered ? row.attendance_count : "Sem lista"}</Cell>
            </Row>
          ))}
        </DataTable>
      )}
    </Panel>
  );
};

const IndicatorsView = ({ options }) => {
  const { api } = useAuth();
  const [ward, setWard] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/portal/meeting-grid/indicators", { params: { ward } })
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);

  const block = (title, values) => (
    <div className="rounded-2xl bg-sand p-4">
      <p className="text-[13px] font-semibold uppercase tracking-wide text-ink-500">{title}</p>
      <div className="mt-3 grid grid-cols-2 gap-3 text-[14px] text-ink-700 sm:grid-cols-3">
        <p>Previstas: <strong>{values.planned}</strong></p>
        <p>Em andamento: <strong>{values.in_progress}</strong></p>
        <p>Realizadas: <strong>{values.done}</strong></p>
        <p>Canceladas: <strong>{values.cancelled}</strong></p>
        <p>Pendentes de confirmação: <strong>{values.pending_confirmation}</strong></p>
        <p>Taxa de realização: <strong>{values.completion_rate === null ? "Sem dados" : `${values.completion_rate}%`}</strong></p>
      </div>
    </div>
  );

  return (
    <Panel testId="grid-indicators-panel" className="space-y-4">
      <PanelHeader
        label={data?.ward_label || "Indicadores"}
        title="Indicadores da Grade"
        hint="Taxa de realização considera apenas reuniões já vencidas."
        actions={
          options?.ward_locked ? null : (
            <select data-testid="indicators-ward" aria-label="Ala" value={ward} onChange={(e) => setWard(e.target.value)} className="h-10 rounded-xl border border-border px-3 text-[14px]">
              <option value="all">Consolidado</option>
              {(options?.wards || []).map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          )
        }
      />
      {data ? (
        <div className="space-y-4">
          {block(`Semana (${formatDay(data.week.start)} a ${formatDay(data.week.end)})`, data.week)}
          {block(`Mês (${formatDay(data.month.start)} a ${formatDay(data.month.end)})`, data.month)}
        </div>
      ) : (
        <p className="text-[14px] text-ink-500">Carregando…</p>
      )}
    </Panel>
  );
};

export default function MeetingGrid() {
  const options = useGridOptions();
  const [tab, setTab] = useState("week");
  const [ward, setWard] = useState(null);

  useEffect(() => {
    if (options && !ward) setWard(options.default_ward);
  }, [options, ward]);

  if (!options || !ward) return null;

  return (
    <div data-testid="meeting-grid-page" className="space-y-6">
      <PageHeader
        label="Atividades com pacientes"
        title="Grade de Reuniões"
        description="Programação das reuniões, grupos e atividades ministradas aos pacientes. Módulo independente das Reuniões Clínicas."
        icon={CalendarDays}
      />
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`grid-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`rounded-full px-4 py-2 text-[14px] font-semibold ${tab === t.key ? "bg-ink-900 text-white" : "bg-sand text-ink-700"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "week" ? <WeekView options={options} ward={ward} setWard={setWard} /> : null}
      {tab === "topics" ? <TopicsView options={options} /> : null}
      {tab === "templates" ? <TemplatesView options={options} ward={ward} setWard={setWard} /> : null}
      {tab === "history" ? <HistoryView options={options} /> : null}
      {tab === "indicators" ? <IndicatorsView options={options} /> : null}
    </div>
  );
}

// Bloco "Suas reuniões de hoje" na tela inicial do funcionário.
export const MyMeetingsToday = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/portal/meeting-grid/today")
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData({ items: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data || data.items.length === 0) return null;

  return (
    <Panel testId="grid-today-panel" className="space-y-3">
      <PanelHeader label="Grade de Reuniões" title="Suas reuniões de hoje" hint="Atividades em que você é responsável." />
      {data.items.map((item) => (
        <div key={item.id} data-testid={`grid-today-${item.id}`} className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3.5 ${item.status === "in_progress" ? "border-sage-600 bg-sage-50" : "border-border bg-white"}`}>
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-ink-900">
              {item.start_time}–{item.end_time} — {item.topic_name}
            </p>
            <p className="text-[13px] text-ink-500">{item.ward_label}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={STATUS_TONE[item.status]}>{item.status_label}</StatusBadge>
            <Link
              to={`/portal/grade-reunioes/${item.id}`}
              data-testid={`grid-today-open-${item.id}`}
              className="inline-flex min-h-[38px] items-center gap-2 rounded-full bg-sage-600 px-4 text-[13.5px] font-semibold text-white"
            >
              <PlayCircle size={15} aria-hidden="true" />
              {item.status === "in_progress" ? "Continuar reunião" : "Abrir reunião"}
            </Link>
          </div>
        </div>
      ))}
    </Panel>
  );
};

// Indicador de presença usado na ficha do paciente e na discussão da Reunião Clínica.
export const AttendanceIndicator = ({ patientId, compact = false }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    api
      .get(`/portal/meeting-grid/patients/${patientId}/attendance`)
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  if (!data) return null;

  return (
    <Panel testId="attendance-indicator" className="space-y-3">
      <PanelHeader label="Grade de Reuniões" title="Presença em reuniões" hint={compact ? undefined : "Calculada automaticamente a partir da Grade de Reuniões."} />
      <button
        type="button"
        data-testid="attendance-indicator-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-2xl bg-sand p-4 text-left"
      >
        <p data-testid="attendance-percent" className="font-display text-[28px] font-semibold text-ink-900">
          {data.label}
        </p>
        <p data-testid="attendance-hint" className="text-[13.5px] text-ink-600">
          {data.hint}
        </p>
      </button>
      {open && data.items.length > 0 ? (
        <div data-testid="attendance-detail" className="space-y-1.5">
          {data.items.map((item) => (
            <p key={item.occurrence_id} className="text-[13.5px] text-ink-700">
              {formatDay(item.date)} — {item.start_time} — {item.topic_name} —{" "}
              <strong className={item.present ? "text-sage-700" : "text-danger"}>{item.present ? "Presente" : "Ausente"}</strong>
            </p>
          ))}
        </div>
      ) : null}
    </Panel>
  );
};
